import { post, isSuccess, extractMessage } from '../../services/api';

function getSavedUser() {
  try {
    return JSON.parse(localStorage.getItem('user')) || null;
  } catch (error) {
    return null;
  }
}

function normalizeUser(user) {
  if (!user) return null;

  return {
    ...user,
    full_name: user.full_name || user.FIO || '',
    phone: user.phone || '',
    club_id: user.club_id ? Number(user.club_id) : null,
    is_admin: Number(user.is_admin || 0),
  };
}

function normalizePhone(value) {
  const digits = String(value || '').replace(/\D/g, '');

  if (digits.length === 10) return `7${digits}`;
  if (digits.length === 11 && digits[0] === '8') return `7${digits.slice(1)}`;
  if (digits.length === 11 && digits[0] === '7') return digits;

  return '';
}

function buildProfilePayload(state, payload = {}) {
  const fullName = payload.full_name ?? state.user?.full_name ?? '';

  return {
    hash: state.user?.hash,
    full_name: fullName,
    FIO: fullName,
    phone: payload.phone ?? state.user?.phone ?? '',
    club_id: payload.club_id ?? state.user?.club_id ?? null,
  };
}

function buildRegistrationPayload(payload = {}) {
  const fullName = String(payload.full_name || payload.FIO || '').trim();
  const rawMethod = String(payload.verification_method || payload.method || 'call');
  const method = ['call', 'telegram'].includes(rawMethod) ? rawMethod : 'call';

  return {
    full_name: fullName,
    FIO: fullName,
    email: String(payload.email || '').trim().toLowerCase(),
    password: String(payload.password || ''),
    password_repeat: String(payload.password_repeat || ''),
    phone: payload.phone || '',
    verification_method: method,
    method,
    club_id: payload.club_id || null,
  };
}

export default {
  namespaced: true,

  state: () => ({
    user: normalizeUser(getSavedUser()),
    loading: false,
    error: '',
    bootstrapped: false,
  }),

  getters: {
    user: (state) => state.user,
    loading: (state) => state.loading,
    error: (state) => state.error,
    bootstrapped: (state) => state.bootstrapped,
    isAuthenticated: (state) => Boolean(state.user && state.user.hash),
    isAdmin: (state) => Number(state.user?.is_admin || 0) === 1,
  },

  mutations: {
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_ERROR(state, value) {
      state.error = value;
    },
    SET_BOOTSTRAPPED(state, value) {
      state.bootstrapped = value;
    },
    SET_USER(state, user) {
      state.user = normalizeUser(user);

      if (state.user) {
        localStorage.setItem('user', JSON.stringify(state.user));
      } else {
        localStorage.removeItem('user');
      }
    },
  },

  actions: {
    async updateProfile({ commit, state }, payload) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', '');

      try {
        if (!state.user?.hash) {
          const message = 'Пользователь не авторизован';
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const result = await post('/user/update-profile-settings', buildProfilePayload(state, payload));

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось обновить профиль');
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const updatedUser = normalizeUser({
          ...state.user,
          ...result.data,
          full_name: result?.data?.full_name || result?.data?.FIO || payload.full_name || state.user.full_name,
          club_id: result?.data?.club_id ?? payload.club_id ?? state.user.club_id,
        });

        commit('SET_USER', updatedUser);
        return { ok: true, user: updatedUser };
      } catch (error) {
        const message = 'Ошибка сети';
        commit('SET_ERROR', message);
        return { ok: false, message };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async bootstrap({ state, commit, dispatch }) {
      if (state.bootstrapped) {
        return true;
      }

      if (!state.user?.hash) {
        commit('SET_BOOTSTRAPPED', true);
        return false;
      }

      const result = await dispatch('relogin');
      commit('SET_BOOTSTRAPPED', true);
      return result.ok;
    },

    async login({ commit, dispatch }, payload) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', '');

      try {
        const phone = normalizePhone(payload.phone || payload.login || '');

        if (!phone) {
          const message = 'Введите корректный номер телефона';
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const result = await post('/user/get-user', {
          phone,
          password: payload.password,
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Неверный телефон или пароль');
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const user = normalizeUser({
          ...result.data,
          phone: result?.data?.phone || payload.phone,
        });

        commit('SET_USER', user);
        dispatch('orders/clearOrdersCache', null, { root: true });
        dispatch('booking/resetState', null, { root: true });
        return { ok: true, user };
      } catch (error) {
        const message = 'Ошибка сети';
        commit('SET_ERROR', message);
        return { ok: false, message };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async registerStart({ commit }, payload) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', '');

      try {
        const result = await post('/user/register-start', buildRegistrationPayload(payload));

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось начать регистрацию');
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        return { ok: true, data: result.data };
      } catch (error) {
        const message = 'Ошибка сети';
        commit('SET_ERROR', message);
        return { ok: false, message };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async registerSendCode({ commit }, payload) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', '');

      try {
        const registrationPayload = buildRegistrationPayload(payload);
        const phone = normalizePhone(registrationPayload.phone);

        if (!phone) {
          const message = 'Введите корректный номер телефона';
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const result = await post('/user/register-send-code', {
          ...registrationPayload,
          phone,
          method: registrationPayload.method,
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось отправить код');
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        return {
          ok: true,
          data: result.data,
          message: extractMessage(result, 'Код отправлен'),
        };
      } catch (error) {
        const message = 'Ошибка сети';
        commit('SET_ERROR', message);
        return { ok: false, message };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async registerConfirmCode({ commit, dispatch }, payload) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', '');

      try {
        const phone = normalizePhone(payload.phone);
        const code = String(payload.code || '').replace(/\D/g, '');

        if (!phone || !code) {
          const message = 'Телефон и код обязательны';
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const result = await post('/user/register-confirm-code', {
          phone,
          code,
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось подтвердить код');
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const user = normalizeUser(result.data);
        if (user?.hash) {
          commit('SET_USER', user);
          dispatch('orders/clearOrdersCache', null, { root: true });
          dispatch('booking/resetState', null, { root: true });
        }

        return { ok: true, user, message: 'Регистрация прошла успешно' };
      } catch (error) {
        const message = 'Ошибка сети';
        commit('SET_ERROR', message);
        return { ok: false, message };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async register({ dispatch }, payload) {
      const startResult = await dispatch('registerStart', payload);
      if (!startResult.ok) return startResult;

      return dispatch('registerSendCode', payload);
    },

    async relogin({ state, commit, dispatch }) {
      if (!state.user?.hash) {
        commit('SET_USER', null);
        return { ok: false };
      }

      try {
        const result = await post('/user/relog-user', {
          email: state.user.email || '',
          hash: state.user.hash,
        });

        if (!isSuccess(result)) {
          commit('SET_USER', null);
          return { ok: false };
        }

        const user = normalizeUser({
          ...state.user,
          ...result.data,
        });

        commit('SET_USER', user);
        dispatch('orders/clearOrdersCache', null, { root: true });
        return { ok: true, user };
      } catch (error) {
        commit('SET_USER', null);
        return { ok: false };
      }
    },

    logout({ commit, dispatch }) {
      commit('SET_USER', null);
      dispatch('orders/clearOrdersCache', null, { root: true });
      dispatch('booking/resetState', null, { root: true });
      commit('SET_ERROR', '');
      return { ok: true };
    },

    clearError({ commit }) {
      commit('SET_ERROR', '');
    },
  },
};
